import { flatMap, isUndefined, max, min, sortedUniq, sum } from 'lodash-es';
import { Attributes } from '../shared/dashboard/base-dashboard/base-dashboard-config';
import { deelVeilig } from '@cumlaude/shared-utils';

type Agg<I, A, T> = {
	init: (i: I, keys: (string | null)[]) => T;
	combine: (as: A[], keys: (string | null)[], all?: T, allFiltered?: T) => T;
};
export type MultiAggregator<Ai extends keyof A, I, A extends { [x in Ai]: N }, N> = Agg<I, A, N> & { attribute: Ai };

export type SingleAggregator<I, T> = {
	init: (i: I, keys: (string | null)[]) => T;
	combine: (as: T[], keys: (string | null)[], all?: T, allFiltered?: T) => T;
};

/**
 * Telt de waarden van kolom `ii` op. Null-waarden worden door `sum` genegeerd.
 * @param ii De numerieke kolom die opgeteld moet worden.
 */
export function sumOver<Ii extends keyof I, I, N extends number | null>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: N }, N> {
	return {
		init: (i) => i[ii],
		combine: (as) => <N>sum(as),
	};
}

/**
 * Telt de waarden van kolom `ii` op, waarbij null als 0 wordt behandeld.
 * @param ii De numerieke kolom die opgeteld moet worden.
 */
export function sumOver0<Ii extends keyof I, I, N extends number | null>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: N }, number> {
	return {
		init: (i) => i[ii] ?? 0,
		combine: (as) => sum(as),
	};
}

/**
 * Telt de waarden van kolom `ii` op voor records die aan de conditie voldoen.
 * @param ii De numerieke kolom die opgeteld moet worden.
 * @param predicate Optionele conditie op basis van record en huidige groeppad (`keys`).
 */
export function sumIf<Ii extends keyof I, I>(
	ii: keyof I,
	predicate?: (i: I, keys: (string | null)[]) => boolean
): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i, keys: (string | null)[]) => ((predicate?.(i, keys) ?? true) ? i[ii] : 0),
		combine: (as) => sum(as),
	};
}

/**
 * Neemt de maximale waarde van kolom `ii` over onderliggende records.
 * @param ii De numerieke kolom waarvan het maximum bepaald wordt.
 */
export function maxOver<Ii extends keyof I, I>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i) => i[ii],
		combine: (as) => max(as) ?? 0,
	};
}

/**
 * Neemt de maximale waarde van kolom `ii` voor records die aan de conditie voldoen.
 * @param ii De numerieke kolom waarvan het maximum bepaald wordt.
 * @param predicate Optionele conditie op basis van record en huidige groeppad (`keys`).
 */
export function maxOverIf<Ii extends keyof I, I>(
	ii: keyof I,
	predicate?: (i: I, keys: (string | null)[]) => boolean
): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i, keys: (string | null)[]) => ((predicate?.(i, keys) ?? true) ? i[ii] : 0),
		combine: (as) => max(as) ?? 0,
	};
}

/**
 * Bepaalt per record eerst een afgeleide waarde en neemt daarover het maximum.
 * @param init Mappingfunctie die de numerieke waarde uit een record haalt.
 */
export function maxOverMapped<I>(init: (v: I) => number): SingleAggregator<I, number> {
	return {
		init: (i) => init(i),
		combine: (as) => max(as) ?? 0,
	};
}

/**
 * Aggregeert niet door, maar gebruikt op hogere niveaus de aangeleverde `_ALL`-waarde.
 * @param ii De kolom waarvan de init-waarde op het laagste niveau wordt gelezen.
 */
export function noAgg<Ii extends keyof I, I, T>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: T }, T | null> {
	return {
		init: (i) => i[ii],
		combine: (as, keys, all) => all ?? null,
	};
}

/**
 * Zoals `noAgg`, maar valt terug op 0 wanneer `_ALL` ontbreekt.
 * @param ii De numerieke kolom waarvan de init-waarde op het laagste niveau wordt gelezen.
 */
export function noAgg0<Ii extends keyof I, I>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i) => i[ii],
		combine: (as, keys, all) => all ?? 0,
	};
}

/**
 * Neem op hogere levels de _ALL_FILTERED als die bestaat, anders _ALL, en anders 0.
 * @param ii De numerieke kolom waarvan de init-waarde op het laagste niveau wordt gelezen.
 */
export function noAggFiltered0<Ii extends keyof I, I>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i) => i[ii],
		combine: (as, keys, all, allFiltered) => allFiltered ?? all ?? 0,
	};
}

/**
 * Leest een waarde uit de `xa`-structuur en aggregeert deze niet verder.
 * @param ii De measure die uit de gekozen xAgg gelezen wordt.
 * @param bitset Optioneel xAgg-bitsetnummer; zonder waarde wordt de eerste xAgg gebruikt.
 */
export function xAgg<Ii extends keyof I, I>(ii: keyof I, bitset?: number): SingleAggregator<I & { [ii in Ii]: number } & { xa: any }, number | null> {
	return {
		init: (i) => {
			if (i.xa === undefined) return null;
			const xaggInit = bitset ? i.xa[bitset] : (Object.values(i.xa)?.[0] as any);
			return xaggInit?.[ii] ?? null;
		},
		combine: (as) => as[0],
	};
}

/**
 * Zoals `xAgg`, maar valt terug op 0 in plaats van null.
 * @param ii De measure die uit de gekozen xAgg gelezen wordt.
 * @param bitset Optioneel xAgg-bitsetnummer; zonder waarde wordt de eerste xAgg gebruikt.
 */
export function xAgg0<Ii extends keyof I, I>(ii: keyof I, bitset?: number): SingleAggregator<I & { [ii in Ii]: number } & { xa: any }, number> {
	return {
		init: (i) => {
			if (i.xa === undefined) return 0;
			const xaggInit = bitset ? i.xa[bitset] : (Object.values(i.xa)?.[0] as any);
			return xaggInit?.[ii] ?? 0;
		},
		combine: (as) => as[0],
	};
}

/**
 * Gaat ervan uit dat er in de xAgg een totaal per jaar staat voor de betreffende backend measure ii. (Dit kan een totaal over alles zijn of bijv. binnen
 * de gekozen groepen en wordt geconfigureerd door de "xa" query parameter op het data endpoint.) Als er meerdere xAggs zijn kan de betreffende bitset
 * worden meegegeven om de juiste te selecteren.
 * @param ii De measure waarvoor per record het deel van het jaartotaal bepaald wordt.
 * @param bitset Optioneel xAgg-bitsetnummer; zonder waarde wordt de eerste xAgg gebruikt.
 */
export function maxDeelVanJaar<Ii extends keyof I, I>(
	ii: keyof I,
	bitset?: number
): SingleAggregator<I & { [ii in Ii]: number } & { xa: any }, number> {
	return {
		init: (i) => {
			if (i.xa === undefined) return 0;
			const xaggInit = bitset ? i.xa[bitset] : (Object.values(i.xa)?.[0] as any);
			const jaarTotaal = xaggInit?.[ii] ?? i[ii];
			return deelVeilig(i[ii], jaarTotaal);
		},
		combine: (as) => max(as) ?? 0,
	};
}

/**
 * Telt `count_records` op voor records die aan de conditie voldoen.
 * @param predicate Conditie op basis van record en huidige groeppad (`keys`).
 */
export function countRecords<I extends { count_records: number }>(
	predicate: (i: I, keys: (string | null)[]) => boolean
): SingleAggregator<I, number> {
	return {
		init: (i, keys: (string | null)[]) => (predicate(i, keys) ? i.count_records : 0),
		combine: (as) => sum(as),
	};
}

/**
 * Telt hoeveel records aan een conditie voldoen.
 * @param predicate Conditie op basis van record en huidige groeppad (`keys`).
 * @param level (optioneel) Begin pas met tellen op dit niveau, d.w.z. alle onderliggende records die aan de conditie voldoen tellen samen mee als 1.
 */
export function countTrue<I>(predicate: (i: I, keys: (string | null)[]) => boolean, level?: number): SingleAggregator<I, number> {
	return {
		init: (i, keys: (string | null)[]) => (predicate(i, keys) ? 1 : 0),
		combine: (as, keys: (string | null)[]) => {
			if (level !== undefined && keys.length > level) return Math.sign(sum(as));
			return sum(as);
		},
	};
}

/**
 * Berekent een gewogen gemiddelde op basis van geaggregeerde child-records.
 * @param attribute Doelkolom waarin het gewogen gemiddelde wordt opgeslagen.
 * @param initialAttribute Bronkolom op recordniveau voor de initiële waarde.
 * @param weightAttribute Kolom met gewichten; standaard `count_records`.
 */
export function weightedAverage<A extends { count_records: number } & { [x in keyof A]: number | null }, I extends { [x in keyof I]: number | null }>(
	attribute: keyof A,
	initialAttribute: keyof I,
	weightAttribute: keyof A = 'count_records'
): MultiAggregator<keyof A, I, A, number | null> {
	return {
		attribute,
		init: (i) => i[initialAttribute],
		combine: (as) => {
			const teller = sum(as.map((a) => (a[attribute] !== null ? a[attribute]! * a[weightAttribute]! : 0)));
			const noemer = sum(as.map((a) => (a[attribute] !== null ? a[weightAttribute]! : 0)));
			return noemer ? teller / noemer : null;
		},
	};
}

/**
 * Berekent een gewogen gemiddelde, maar gebruikt op hogere niveaus eerst `_ALL` wanneer aanwezig.
 * @param attribute Doelkolom waarin het gewogen gemiddelde wordt opgeslagen.
 * @param initialAttribute Bronkolom op recordniveau voor de initiële waarde.
 */
export function weightedAverageAll<
	A extends { count_records: number } & { [x in keyof A]: number | null },
	I extends { [x in keyof I]: number | null },
>(attribute: keyof A, initialAttribute: keyof I): MultiAggregator<keyof A, I, A, number | null> {
	return {
		attribute,
		init: (i) => i[initialAttribute],
		combine: (as, _keys, all) => {
			if (all !== undefined) return all;
			const teller = sum(as.map((a) => (a[attribute] !== null ? a[attribute]! * a.count_records : 0)));
			const noemer = sum(as.map((a) => (a[attribute] !== null ? a.count_records : 0)));
			return noemer ? teller / noemer : null;
		},
	};
}

/**
 * Geeft op lagere niveaus 0, op het "level" het aantal onderliggende records en op hogere niveaus het maximum van deze getallen.
 * @param attribute Doelkolom waarin het maximum komt.
 */
export function maxAtLevel<Ai extends keyof A, A extends Attributes & { [x in Ai]: number }, I extends Attributes>(
	attribute: Ai,
	level: () => number
): MultiAggregator<Ai, I, A, number> {
	return {
		attribute,
		init: (i, keys) => (keys.length === level() ? i.count_records : 0),
		combine: (as, keys) => (keys.length === level() ? sum(as.map((a) => a.count_records)) : (max(as.map((a) => a[attribute])) ?? 0)),
	};
}

/**
 * Geeft op lagere niveaus 0, op het "level" de waarde van een ander ("over") attribuut en op hogere niveaus het maximum van deze getallen. Hierbij wordt er wel vanuit gegaan dat het "over" attribuut een som is over het niveau eronder (deze beperking komt voort uit de dataflow van een (Multi)Aggregator)
 * @param attribute Doelkolom waarin het maximum komt.
 * @param over Bronkolom waarover op het opgegeven niveau het maximum bepaald wordt.
 */
export function maxOverAtLevel<
	Ai extends keyof A,
	Oi extends keyof A & keyof I,
	A extends Attributes & { [x in Ai]: number } & { [x in Oi]: number },
	I extends Attributes & { [x in Oi]: number },
>(attribute: Ai, over: Oi, level: () => number): MultiAggregator<Ai, I, A, number> {
	return {
		attribute,
		init: (i, keys) => (keys.length === level() ? i[over] : 0),
		combine: (as, keys) => (keys.length === level() ? sum(as.map((a) => a[over])) : (max(as.map((a) => a[attribute])) ?? 0)),
	};
}

/**
 * Telt op lagere niveaus de waarden van de bronkolom op. Boven het opgegeven niveau neemt hij het maximum.
 * @param attribute Doelkolom.
 * @param over Bronkolom.
 * @param predicate (optioneel) conditie op basis waarvan een waarde op het laagste niveau meegerekend wordt
 */
export function maxSumOfStacksIf<
	Ai extends keyof A,
	Oi extends keyof A & keyof I,
	A extends Attributes & { [x in Ai]: number } & { [x in Oi]: number },
	I extends Attributes & { [x in Oi]: number },
>(attribute: Ai, over: Oi, level: () => number, predicate?: (i: I, keys: (string | null)[]) => boolean): MultiAggregator<Ai, I, A, number> {
	return {
		attribute,
		init: (i, keys) => (keys.length >= level() && (predicate?.(i, keys) ?? true) ? i[over] : 0),
		combine: (as, keys) => (keys.length >= level() ? sum(as.map((a) => a[attribute])) : (max(as.map((a) => a[attribute])) ?? 0)),
	};
}

/**
 * Bepaalt de minimumwaarde van kolom `ii` over onderliggende records.
 * @param ii De numerieke kolom waarvan het minimum bepaald moet worden.
 */
export function minOver<Ii extends keyof I, I>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i) => i[ii],
		combine: (as) => min(as) ?? 0,
	};
}

/**
 * Aggregeert niet, maar geeft de stringwaardes op laagste en `_ALL`-niveaus door.
 */
export function noAggString<I>(ii: keyof I): SingleAggregator<I, string> {
	return {
		init: (i) => {
			return <string>i[ii];
		},
		combine: (as, keys, all) => {
			return all ?? '';
		},
	};
}

/**
 * Neemt alle waarden van de onderliggende records, splitst ze op ", ", voegt ze samen tot 1 array, sorteert en maakt ze uniek, en voegt ze dan weer samen tot een string gescheiden door ", ".
 * @param ii De te aggregeren string kolom. Deze kan meerdere waarden bevatten gescheiden door ", " (zoals bijv. or_m_nm_vestigingen en vkk_m_nm_opleidingen).
 * @param predicate De conditie waar een rij aan moet voldoen.
 */
export function aggArrayString<I>(ii: keyof I, predicate?: (i: I, keys: (string | null)[]) => boolean): SingleAggregator<I, string> {
	return {
		init: (i, keys) => {
			return (predicate?.(i, keys) ?? true) ? String(i[ii] ?? '') : '';
		},
		combine: (as) => {
			if (isUndefined(as)) return '';

			const stringArray = flatMap(as.map((val) => val.split(', '))).filter(Boolean);
			stringArray.sort((a, b) => a.localeCompare(b));
			return sortedUniq(stringArray).join(', ');
		},
	};
}

/**
 * Speciale aggregatie voor het aantal decimalen bij het middelen van cijfers.
 * Volgens de significantieregels is het aantal decimalen van een gemiddeld cijfer het
 * minimum van de aantallen decimalen van de onderliggende cijfers. Echter omdat het
 * nuttig is om te weten of bijv. een klassengemiddelde van afgeronde eindcijfers 5,5 of 6,4
 * is, hanteren we bij gemiddelde cijfers (count_records > 1) een minimum van 1 decimaal.
 * @param ii De te aggregeren numerieke kolom waarvan het aantal decimalen bepaald moet worden.
 */
export function aggDecimals<Ii extends keyof I, I extends Attributes>(ii: keyof I): SingleAggregator<I & { [ii in Ii]: number }, number> {
	return {
		init: (i) => (i.count_records === 1 ? i[ii] : Math.max(i[ii], 1)),
		combine: (as: number[]) => Math.max(min(as) ?? 0, 1),
	};
}
